import { useNotificationStore } from '@/legacy/store/modules/notification'
import {
  NotificationActions,
  notificationActionToPastTense,
  NotificationType,
} from '@/legacy/types/notifications'

export type ApiCallNotificationOptions = {
  // The action being performed in the API call
  operation: NotificationActions
  // The name of the resource involved in the API call
  resourceName: string
  // Optional notification type for the error notification (defaults to DANGER)
  errorType?: NotificationType
}

/**
 * Shows an error notification for a failed API call.
 * @param error - The error that occurred.
 * @param options
 */
export function showErrorNotification<TError = Error>(
  error: TError,
  options: ApiCallNotificationOptions
) {
  const {
    operation,
    resourceName,
    errorType = NotificationType.DANGER,
  } = options
  useNotificationStore().setNotification({
    message: `Error ${operation} ${resourceName}`,
    type: errorType,
    error: `Error ${operation} ${resourceName}: ${error}`,
  })
}

/**
 * Returns callbacks for showing success and error notifications for API calls.
 *
 * Example usage:
 *
 * const { showSuccessNotification, showErrorNotification } = getApiCallNotificationCallbacks({
 *   operation: NotificationActions.CREATE,
 *   resourceName: 'item',
 *   errorType: NotificationType.WARNING,
 * })
 *
 * // when successful:
 * showSuccessNotification() // shows "Successfully created item" notification
 *
 * // when error occurs:
 * showErrorNotification(error) // shows "Error creating item" notification
 * @param options - Options for the notifications.
 */
export function getApiCallNotificationCallbacks<TError = Error>(
  options: ApiCallNotificationOptions
) {
  const { operation, resourceName, errorType } = options
  const operationPastTense = notificationActionToPastTense[operation]
  return {
    showSuccessNotification: () => {
      useNotificationStore().setNotification({
        message: `Successfully ${operationPastTense} ${resourceName}.`,
        type: NotificationType.SUCCESS,
      })
    },
    showErrorNotification: (err: TError) => {
      showErrorNotification(err, {
        operation,
        resourceName,
        errorType,
      })
    },
  }
}
